import { ChevronDown } from "lucide-react";

import { useTran } from "@/hooks";
import { FormField, SelectField } from "@/components/common";
import { PERIOD_OPTION } from "@/lib/constants";

import { ensureArraySize, ensureFileArraySize, periodLabels } from "../utils";
import { useNxplanScopeContext } from "./NxplanScopeContext";
import FileUploadCard from "./FileUploadCard";

const ScopeEmissionForm = ({ scopeKey, label }) => {
  const { t } = useTran();
  const {
    accordionOpen,
    setAccordionOpen,
    freq,
    setFreq,
    emissions,
    setEmissions,
    refFiles,
    setRefFiles,
    refFileName,
    refFileLink,
    absolutePath,
    sanitizeNumeric,
  } = useNxplanScopeContext();

  const frequency = freq[scopeKey] || "yearly";
  const isOpen = Boolean(accordionOpen[scopeKey]);
  const displayLabel = t(label);
  const periodList = periodLabels(frequency);

  const handleToggle = () =>
    setAccordionOpen((prev) => ({ ...prev, [scopeKey]: !prev[scopeKey] }));

  const handleFrequencyChange = (value) => {
    setFreq((prev) => ({ ...prev, [scopeKey]: value }));
    const size = value === "monthly" ? 12 : value === "quarterly" ? 4 : 1;
    setEmissions((prev) => ({
      ...prev,
      [scopeKey]: ensureArraySize(prev[scopeKey] || [""], size),
    }));
    setRefFiles((prev) => ({
      ...prev,
      [scopeKey]: ensureFileArraySize(prev[scopeKey] || [null], size),
    }));
  };

  return (
    <div className="rounded-radius border border-border bg-muted/30">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between px-4 py-3"
        onClick={handleToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleToggle();
          }
        }}
      >
        <span className="font-semibold text-foreground">
          {displayLabel} (tCO₂e)
        </span>
        <div className="flex items-center gap-3">
          <div className="w-32" onClick={(event) => event.stopPropagation()}>
            <SelectField
              value={frequency}
              onValueChange={handleFrequencyChange}
              options={PERIOD_OPTION}
            />
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="px-4 pb-4 pt-2 text-sm text-muted-foreground">
          <p>
            {t("Configure data cadence and details for")}{" "}
            {displayLabel.toLowerCase()}. {t("Frequency set to")}{" "}
            <span className="font-medium capitalize">{frequency}</span>.
          </p>
          <div className="mt-3 space-y-3">
            {periodList.map((periodLabel, periodIndex) => (
              <div
                key={periodLabel}
                className="grid grid-cols-1 gap-4 md:grid-cols-5 items-end"
              >
                <div className="md:col-span-2">
                  <FormField
                    label={periodLabel}
                    type="text"
                    inputMode="decimal"
                    value={
                      (emissions[scopeKey] &&
                        emissions[scopeKey][periodIndex]) ??
                      ""
                    }
                    onChange={(event) =>
                      setEmissions((prev) => {
                        const arr = ensureArraySize(
                          prev[scopeKey] || [""],
                          periodList.length,
                        );
                        arr[periodIndex] = sanitizeNumeric(event.target.value);
                        return { ...prev, [scopeKey]: arr };
                      })
                    }
                    placeholder={t("Enter value")}
                  />
                </div>
                <div className="md:col-span-3">
                  <FileUploadCard
                    compact
                    className="mt-1 md:mt-[6px]"
                    value={
                      refFiles[scopeKey]?.[periodIndex]
                        ? {
                            file: refFiles[scopeKey][periodIndex],
                            uploadedAt: new Date(),
                          }
                        : {
                            url:
                              (refFileLink[scopeKey] &&
                                refFileLink[scopeKey][periodIndex]) ??
                              "",
                            name:
                              (refFileName[scopeKey] &&
                                refFileName[scopeKey][periodIndex]) ??
                              "",
                          }
                    }
                    baseUrl={absolutePath}
                    onChange={(val) => {
                      const file = val?.file ?? null;
                      setRefFiles((prev) => {
                        const arr = ensureFileArraySize(
                          prev[scopeKey] || [null],
                          periodList.length,
                        );
                        arr[periodIndex] = file;
                        return { ...prev, [scopeKey]: arr };
                      });
                    }}
                    accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg"
                    label={t("Reference file (optional)")}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopeEmissionForm;
